import React, { useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faBan,
  faCircleCheck,
  faIdCard,
  faMoneyBillWave,
  faReceipt,
  faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons"
import { ExitCheckResponse, useCheckCustomerExit } from "../../../hooks/Costumer/useCheckCustomerExit"
import InputField from "../../../components/InputField/InputField"
import Button from "../../../components/Button/Button"
import { THEME } from "../../../configuration/Theme"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import { useTranslation } from "react-i18next"

export const CustomerExitCheckPage: React.FC = () => {
  const { t } = useTranslation("customer")

  const [cpf, setCpf] = useState("")

  const [result, setResult] = useState<ExitCheckResponse | null>(null)

  const [isSearching, setIsSearching] = useState(false)

  const { handleCheckCustomerExit } = useCheckCustomerExit()

  const handleCheck = async () => {
    const normalizedCpf = cpf.trim()

    if (!normalizedCpf) {
      return
    }

    setIsSearching(true)

    await handleCheckCustomerExit(normalizedCpf, setResult)

    setIsSearching(false)
  }

  return (
    <Animate type={AnimationTypes.fadeInTraslate}>
      <div className="w-full h-[calc(100%-25px)] overflow-y-auto">
        <div className="ring-2 ring-orange-100 m-2 rounded-lg bg-white shadow-none border-0 p-3 sm:p-4 md:p-6">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-2xl bg-orange-100 text-orange-600 flex items-center justify-center">
              <FontAwesomeIcon icon={faIdCard} />
            </div>

            <div>
              <h1 className="text-xl md:text-2xl font-extrabold text-gray-900">{t("exit_check.title")}</h1>

              <p className="text-sm text-gray-500 mt-1">{t("exit_check.subtitle")}</p>
            </div>
          </div>

          <Animate type={AnimationTypes.fadeInTraslate} duration="1.5s">
            <div className="mt-5 flex flex-col sm:flex-row gap-3 items-stretch">
              <div className="w-full">
                <InputField
                  type="number"
                  value={cpf}
                  onChange={(event) => setCpf(event.target.value)}
                  placeholder={t("exit_check.input_placeholder")}
                  title={t("exit_check.input_title")}
                  icon={faIdCard}
                  descriptionKey="interactionDescriptions:pages.fluxo.customer.customerExitCheckPage.input.placeholder"
                />
              </div>

              <div className="w-full sm:w-[220px] flex items-end">
                <Button
                  label={isSearching ? t("exit_check.button_loading") : t("exit_check.button_default")}
                  onClick={() => void handleCheck()}
                  disabled={isSearching || !cpf.trim()}
                  bgColor={THEME.colors.buttonBGSaveColor}
                  descriptionKey="interactionDescriptions:pages.fluxo.customer.customerExitCheckPage.button.loading"
                />
              </div>
            </div>
          </Animate>

          {result && (
            <Animate type={AnimationTypes.fadeInTraslate} duration="1.8s">
              <div className="mt-6">
                {!result.customerFound ? (
                  <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 text-gray-700">
                    <div className="text-[14px] font-semibold flex items-center gap-2">
                      <FontAwesomeIcon icon={faBan} className="text-gray-500" />
                      {t("exit_check.customer_not_found")}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div
                      className={`rounded-lg p-4 border-2 shadow-sm ${
                        result.hasActiveDebit ? " border-red-200" : "bg-green-50 border-green-200"
                      }`}
                    >
                      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                          <h2 className="text-lg font-bold text-gray-900">{result.customerName}</h2>

                          <span className="text-sm text-gray-600">
                            {t("exit_check.customer_cpf")}: {result.cpf || "—"}
                          </span>
                        </div>

                        {result.hasActiveDebit ? (
                          <span className="px-3 py-1 rounded-full text-sm font-bold bg-red-200 text-red-800 flex items-center gap-2">
                            <FontAwesomeIcon icon={faTriangleExclamation} />
                            {t("exit_check.with_active_debit")}
                          </span>
                        ) : (
                          <span className="px-3 py-1 rounded-full text-sm font-bold bg-green-100 text-green-700 flex items-center gap-2">
                            <FontAwesomeIcon icon={faCircleCheck} />
                            {t("exit_check.no_debits")}
                          </span>
                        )}
                      </div>

                      {result.hasActiveDebit && (
                        <div className="mt-3 text-red-700 font-semibold flex items-center gap-2">
                          <FontAwesomeIcon icon={faMoneyBillWave} />
                          {t("exit_check.total_open")}: {result.totalDebit.toMoney()}
                        </div>
                      )}
                    </div>

                    {result.hasActiveDebit && (
                      <div className="rounded-lg border-2 border-red-200 overflow-hidden">
                        <div className="px-4 py-3 bg-red-50 border-b border-red-100 font-semibold text-red-700 flex items-center gap-2">
                          <FontAwesomeIcon icon={faReceipt} />
                          {t("exit_check.orders_with_debit")}
                        </div>

                        <div className="divide-y divide-red-100">
                          {result.activeDebitOrders.map((order, index) => (
                            <Animate
                              key={order.orderId}
                              type={AnimationTypes.fadeInTraslate}
                              duration={1.8 + index * 0.4 + "s"}
                            >
                              <div className="px-4 py-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                                <div>
                                  <div className="font-semibold text-gray-800">
                                    {t("exit_check.order_prefix")} #{order.orderId}
                                  </div>
                                  <div className="text-sm text-gray-500">
                                    {order.date.toReadableDate()}{" "}
                                    {order.servicePointName ? `• ${t("exit_check.service_point_prefix")} ${order.servicePointName}` : ""}
                                  </div>
                                  {order.description && (
                                    <div className="text-xs text-gray-500 mt-1">{order.description}</div>
                                  )}
                                </div>

                                <div className="text-sm font-bold text-red-700">{order.totalFinal.toMoney()}</div>
                              </div>
                            </Animate>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </Animate>
          )}
        </div>
      </div>
    </Animate>
  )
}
